import { motion } from 'framer-motion';
import ScrollIndicator from './ScrollIndicator';
import { useState, useEffect } from 'react';
import { AuthModal } from './AuthModal';
import { useAuth } from '../contexts/AuthContext';
import { useRegion } from '../contexts/RegionContext';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

export default function Hero() {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const { user } = useAuth();
  const { t } = useRegion();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handler, { passive: true });
    return () => window.removeEventListener('resize', handler);
  }, []);

  const handleAuthClick = (mode: 'login' | 'signup') => {
    setAuthMode(mode);
    setIsAuthModalOpen(true);
  };

  // تنظیمات انیمیشن برای موبایل و دسکتاپ
  const animationConfig = {
    initial: { opacity: 0, y: isMobile ? 10 : 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: isMobile ? 0.5 : 0.8 }
  };

  return (
    <>
      <Helmet>
        <title>Active Legend | برترین استودیو بازی سازی ایرانی و مرجع گیمینگ</title>
        <meta
          name="description"
          content="استودیو بازی سازی اکتیو لجند (Active Legend)؛ برترین استودیو مستقل بازی سازی ایرانی، خالق بازی‌های رقابتی کامپیوتر با آنریل انجین ۵ (Stop Duck) و مجله تخصصی بازی‌سازی در ایران."
        />
        <meta
          name="keywords"
          content="استودیو بازی سازی, استودیو بازی سازی ایرانی, استودیو گیم سازی, استودیوهای بازی سازی ایران, ساخت بازی در ایران, Active Legend, اکتیو لجند, بازی سازی با آنریل انجین 5, بازی کامپیوتر ایرانی, توسعه دهنده بازی ایرانی, Stop Duck, محمد مهدی مولایاری"
        />
        <link rel="canonical" href="https://activelegend.ir/" />
        <meta property="og:title" content="Active Legend | برترین استودیو بازی سازی ایرانی و مرجع گیمینگ" />
        <meta
          property="og:description"
          content="استودیو مستقل بازی‌سازی اکتیو لجند؛ پیشگام در ساخت بازی‌های مدرن ایرانی با Unreal Engine 5 و مرجع مقالات تخصصی بازی‌سازی."
        />
        <meta property="og:url" content="https://activelegend.ir/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://activelegend.ir/AE-logo.png" />
        <meta name="twitter:title" content="Active Legend | برترین استودیو بازی سازی ایرانی" />
        <meta
          name="twitter:description"
          content="استودیو مستقل بازی‌سازی اکتیو لجند؛ خالق بازی‌های رقابتی مدرن و مرجع تخصصی اخبار و مقالات بازی‌سازی."
        />
        <meta name="twitter:image" content="https://activelegend.ir/AE-logo.png" />
      </Helmet>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'Active Legend',
            alternateName: 'استودیو بازی سازی ایرانی اکتیو لجند',
            url: 'https://activelegend.ir',
            description: 'استودیو مستقل بازی سازی ایرانی، خالق بازی‌های رقابتی مدرن کامپیوتر با آنریل انجین ۵.',
            publisher: {
              '@type': 'Organization',
              name: 'Active Legend',
              logo: { '@type': 'ImageObject', url: 'https://activelegend.ir/AE-logo.png' },
            },
            potentialAction: { '@type': 'SearchAction', target: 'https://activelegend.ir/blog?q={search_term_string}', 'query-input': 'required name=search_term_string' },
          }),
        }}
      />
      <header className="relative min-h-screen flex items-center justify-center">
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/80 to-black/95 pointer-events-none"></div>
      
      <motion.div
        {...animationConfig}
        className="relative z-10 text-center max-w-4xl mx-auto px-4"
      >
        <motion.img
          src={import.meta.env.BASE_URL + 'AE logo.svg'}
          alt="Active Legend - استودیو بازی سازی ایرانی"
          className="w-36 h-36 sm:w-56 sm:h-56 md:w-64 md:h-64 mx-auto mb-6"
          initial={{ scale: isMobile ? 0.8 : 0 }}
          animate={{ scale: 1 }}
          transition={{ 
            type: isMobile ? "tween" : "spring",
            duration: isMobile ? 0.5 : 1 
          }}
        />

        <div className="inline-block px-4 py-1.5 rounded-full bg-primary/10 border border-primary/30 text-primary text-xs sm:text-sm font-bold mb-4">
          {t('hero.badge')}
        </div>
        
        <motion.h1
          className="text-4xl sm:text-5xl md:text-6xl font-black mb-4 text-white tracking-tight"
          {...animationConfig}
          transition={{ ...animationConfig.transition, delay: isMobile ? 0.1 : 0.3 }}
        >
          {t('hero.title')}
        </motion.h1>
        
        <motion.p
          className="text-lg sm:text-xl md:text-2xl mb-8 text-gray-300 max-w-2xl mx-auto leading-relaxed"
          {...animationConfig}
          transition={{ ...animationConfig.transition, delay: isMobile ? 0.2 : 0.5 }}
        >
          {t('hero.subtitle')}
        </motion.p>
        
        <motion.div
          className="flex items-center justify-center gap-3 sm:gap-4 flex-wrap"
          {...animationConfig}
          transition={{ ...animationConfig.transition, delay: isMobile ? 0.3 : 0.7 }}
        >
          <Link to="/games">
            <motion.button
              className="btn-primary"
              whileHover={{ scale: isMobile ? 1.02 : 1.05 }}
              whileTap={{ scale: isMobile ? 0.98 : 0.95 }}
            >
              {t('hero.viewGames')}
            </motion.button>
          </Link>
          {!user && (
            <>
              <motion.button
                className="btn-secondary"
                whileHover={{ scale: isMobile ? 1.02 : 1.05 }}
                whileTap={{ scale: isMobile ? 0.98 : 0.95 }}
                onClick={() => handleAuthClick('signup')}
              >
                {t('hero.signup')}
              </motion.button>
              <motion.button
                className="btn-secondary"
                whileHover={{ scale: isMobile ? 1.02 : 1.05 }}
                whileTap={{ scale: isMobile ? 0.98 : 0.95 }}
                onClick={() => handleAuthClick('login')}
              >
                {t('hero.login')}
              </motion.button>
            </>
          )}
        </motion.div>
      </motion.div>
      
      <ScrollIndicator />

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        initialMode={authMode}
      />
      </header>
    </>
  );
}